/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import type { FC } from 'react'
import React, { useCallback, useState } from 'react'

import type { SvgIconResource } from './svg-icons'
import svgIcons from './svg-icons'
import type { IconProps } from './types'
import useResponsiveValue from '../../../hooks/use-responsive-value'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { get } from '../../../utils/object'
import { getSvg, omitSvg } from '../../../utils/props-utils'
import { isValidUnit, remToPx, resolveSize } from '../../../utils/size-utils'
import { useUniqueId } from '../../../utils/unique-id'
import Box from '../box/box'
import FlexInline from '../flex/flex-inline'
import Svg from '../svg'

const Icon: FC<IconProps & { theme?: Theme; __css?: object }> =
  React.forwardRef<
    HTMLDivElement,
    IconProps & { theme?: Theme; __css?: object }
  >(function Icon(props, ref) {
    const theme = useSafeThemeUI(props)
    const {
      id,
      className,
      size = 'sm',
      icon,
      glowEffect,
      highlight,
      secondaryColor,
      onHover,
      openSize,
      noWrapper,
      children,
      viewBox,
      height,
      width,
      ...rest
    } = props
    const identifier = id || useUniqueId('icon')
    const _iconSize = resolveSize(size, get(theme, 'sizes.icon'))
    const _size = Array.isArray(_iconSize)
      ? useResponsiveValue(_iconSize, { updateOnResizeChangeStopPeriod: 50 })
      : _iconSize

    const _icon = ((svgIcons as any)[icon] as SvgIconResource) ?? 24

    const [isHover, setHover] = useState(false)
    const handleMouseEnter = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setHover(true)
        onHover && onHover(true, event.currentTarget)
      },
      [onHover]
    )
    const handleMouseLeave = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setHover(false)
        onHover && onHover(false, event.currentTarget)
      },
      [onHover]
    )

    const useSize = typeof _icon.size === 'number' || isValidUnit(_icon.size)
    const iconSize =
      !openSize && useSize ? { size: remToPx(_size) ?? _icon.size } : {}
    const WrapperComponent = openSize ? Box : FlexInline
    const content = (
      <Svg
        role="img"
        className={'csg-tui-icon-svg'}
        __css={{
          overflow: 'visible'
        }}
        {...{
          viewBox: viewBox ?? _icon.viewBox,
          ...iconSize,
          ...(!openSize && typeof _icon.size === 'object'
            ? {
                width: (width as any) ?? _icon.size.width,
                height: (height as any) ?? _icon.size.height
              }
            : {})
        }}
      >
        {highlight || (glowEffect && isHover) ? (
          <defs>
            <filter id={`glow-${identifier}`}>
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
        ) : null}
        {children ?? (
          <_icon.element
            {...(noWrapper ? rest : getSvg(rest))}
            secondaryColor={secondaryColor}
            filter={
              highlight || (glowEffect && isHover)
                ? `url(#glow-${identifier})`
                : undefined
            }
          />
        )}
      </Svg>
    )
    return !noWrapper ? (
      <WrapperComponent
        ref={ref}
        className={classNames('csg-tui-icon', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...{
          alignSelf: 'center',
          ...(iconSize.size
            ? {
                width: iconSize.size,
                height: iconSize.size
              }
            : {}),
          ...omitSvg(rest),
          ...(openSize ? { width: '100%' } : {}),
          ...((highlight || (glowEffect && isHover)) && secondaryColor
            ? { color: secondaryColor }
            : {})
        }}
      >
        {content}
      </WrapperComponent>
    ) : (
      content
    )
  })

export default Icon
